import PackageOrderAffiliateList from "issara-sdk/apis/PackageOrderAffiliateList_apps_BIL";
import PackageList from "issara-sdk/apis/PackageList_core";

import PackageOrderAffiliateSerializer from "issara-sdk/types/PackageOrderAffiliateSerializer_apps_BIL";

import {
  ActionHandler,
  BaseSeqState,
  BLC_STATUS,
  GenerateActionTypes,
  generatePrefixedActions,
  PickMainState,
} from "react-lib/apps/HISV3/common/CommonInterface";

import { convertThaiDateToISO } from "react-lib/utils/dateUtils";

/** ============== Type-Defining Constants =============  */

export const ACTIONS = {
  CLEAR: "CLEAR",
  SEARCH: "SEARCH",
} as const;

export const SEQUENCE_NAME = "AffiliateCommission" as const;
export const MAIN_CARD_NAME = "CardAffiliateCommission" as const;

export const ROWS_PER_PAGE = 20;

export const COMMISSION_STATUS_OPTIONS = [
  { key: "ALL", text: "ทั้งหมด", value: "" },
  { key: "PENDING_PERFORM", text: "รอดำเนินการ", value: "PENDING_PERFORM" },
  { key: "PENDING_REVIEW", text: "รอตรวจสอบ", value: "PENDING_REVIEW" },
  { key: "APPROVED", text: "อนุมัติ", value: "APPROVED" },
  { key: "PAID", text: "เบิกจ่ายแล้ว", value: "PAID" },
  { key: "CANCELED", text: "ยกเลิก", value: "CANCELED" },
];

/** =============== Types and Interfaces ===============  */
export type State = Partial<{
  AffiliateCommissionSequence?: AffiliateCommissionType;
}>;

export type AffiliateCommissionFilterType = {
  commissionStatus: string;
  createdEnd: string;
  createdStart: string;
  package: number | null;
  paidEnd: string;
  paidStart: string;
  patient: number | null;
  performedEnd: string;
  performedStart: string;
  user: number | null;
};

export type AffiliateCommissionType = Partial<{
  sequenceIndex: "Action" | "START" | null;
  activePage: number;
  commissionList: PackageOrderAffiliateSerializer[];
  filter: AffiliateCommissionFilterType;
  packageNameDict: Record<number, string>;
  total: number;
}> | null;

export type PickedState = PickMainState<"buttonLoadCheck" | "errorMessage">;

export type Event = { message: "GetMasterData"; params: Record<string, any> };

export type Data = { device?: number; division?: number };

export type SeqState = BaseSeqState<SequenceName>;

export type Actions = typeof ACTIONS;

type SequenceName = typeof SEQUENCE_NAME;

type ActionPayloadMap = {
  [ACTIONS.CLEAR]: {};
  [ACTIONS.SEARCH]: { page?: number };
};

type ActionTypes = GenerateActionTypes<
  Actions,
  ActionPayloadMap,
  SequenceName,
  PickedState & State
>;

type ActionType = ActionTypes["ActionType"];

export type RunSequence = ActionTypes["RunSequence"];

export type SetProp = ActionTypes["SetProp"];

export type OnEvent = ActionTypes["OnEvent"];

type Params<A extends ActionType["action"]> = ActionTypes["Params"][A];

export type Handler<P = unknown, R = void> = ActionHandler<PickedState & State, Event, Data, P, R>;

/** ===================== Constants ====================  */
export const StateInitial: State = {
  AffiliateCommissionSequence: null,
};

export const DataInitial: Data = {};

export const BTN_ACTS = generatePrefixedActions(ACTIONS, SEQUENCE_NAME);

const PACKAGE_DICT_LIMIT = 1000;

const INITIAL_FILTER: AffiliateCommissionFilterType = {
  commissionStatus: "",
  createdEnd: "",
  createdStart: "",
  package: null,
  paidEnd: "",
  paidStart: "",
  patient: null,
  performedEnd: "",
  performedStart: "",
  user: null,
};

/** ===================== Functions ====================  */
export const Start: Handler<SeqState> = async (controller, params) => {
  const state = controller.getState();

  const [packageRes] = await PackageList.list({
    apiToken: controller.apiToken,
    extra: { division: controller.data.division },
    params: { limit: PACKAGE_DICT_LIMIT },
  });

  const packageNameDict: Record<number, string> = Object.fromEntries(
    (packageRes?.items || [])
      .filter((item: any) => item.id && item.name)
      .map((item: any) => [item.id, item.name])
  );

  controller.setState(
    {
      AffiliateCommissionSequence: {
        ...state.AffiliateCommissionSequence,
        sequenceIndex: "Action",
        activePage: 1,
        commissionList: [],
        filter: { ...INITIAL_FILTER },
        packageNameDict,
        total: 0,
      },
    },
    () => Action(controller, { ...params, action: ACTIONS.SEARCH })
  );
};

/** ====================================================  */
/**                        Action                         */
/** ====================================================  */
export const Action: Handler<ActionType> = async (controller, params) => {
  const actionHandlers: Partial<{ [K in ActionType["action"]]: Handler<Params<K>> }> = {
    [ACTIONS.CLEAR]: HandleClear,
    [ACTIONS.SEARCH]: HandleSearch,
  };

  const { action } = params;

  const handler = actionHandlers[action] as Handler<Params<typeof action>> | undefined;

  if (handler) {
    handler(controller, params);
  } else {
    console.error(`No handler found for action: ${action}`);
  }
};

/** ====================================================  */
/**                        Handler                        */
/** ====================================================  */
const HandleSearch: Handler<Params<"SEARCH">> = async (controller, params) => {
  const state = controller.getState();

  controller.setState({
    buttonLoadCheck: { ...state.buttonLoadCheck, [BTN_ACTS.SEARCH]: BLC_STATUS.LOADING },
    errorMessage: { ...state.errorMessage, [BTN_ACTS.SEARCH]: null },
  });

  const filter = state.AffiliateCommissionSequence?.filter || INITIAL_FILTER;
  const activePage = params.page || 1;

  const [commissionRes, commissionErr] = await PackageOrderAffiliateList.list({
    apiToken: controller.apiToken,
    extra: { device: controller.data.device, division: controller.data.division },
    params: removeEmptyParams({
      commission_status: filter.commissionStatus,
      limit: ROWS_PER_PAGE,
      offset: (activePage - 1) * ROWS_PER_PAGE,
      package: filter.package,
      patient: filter.patient,
      po_created_end_date: convertThaiDateToISO(filter.createdEnd),
      po_created_start_date: convertThaiDateToISO(filter.createdStart),
      po_paid_end_date: convertThaiDateToISO(filter.paidEnd),
      po_paid_start_date: convertThaiDateToISO(filter.paidStart),
      po_performed_end_date: convertThaiDateToISO(filter.performedEnd),
      po_performed_start_date: convertThaiDateToISO(filter.performedStart),
      user: filter.user,
    }),
  });

  const currentState = controller.getState();

  if (commissionErr) {
    controller.setState({
      AffiliateCommissionSequence: {
        ...currentState.AffiliateCommissionSequence,
        activePage,
        commissionList: [],
        total: 0,
      },
      buttonLoadCheck: {
        ...currentState.buttonLoadCheck,
        [BTN_ACTS.SEARCH]: BLC_STATUS.ERROR,
      },
      errorMessage: {
        ...currentState.errorMessage,
        [BTN_ACTS.SEARCH]: normalizeError(commissionErr),
      },
    });

    return;
  }

  controller.setState({
    AffiliateCommissionSequence: {
      ...currentState.AffiliateCommissionSequence,
      activePage,
      commissionList: commissionRes?.items || [],
      total: commissionRes?.total || 0,
    },
    buttonLoadCheck: {
      ...currentState.buttonLoadCheck,
      [BTN_ACTS.SEARCH]: BLC_STATUS.SUCCESS,
    },
  });
};

const HandleClear: Handler<Params<"CLEAR">> = async (controller, params) => {
  const state = controller.getState();

  controller.setState(
    {
      AffiliateCommissionSequence: {
        ...state.AffiliateCommissionSequence,
        activePage: 1,
        filter: { ...INITIAL_FILTER },
      },
      errorMessage: { ...state.errorMessage, [BTN_ACTS.SEARCH]: null },
    },
    () => HandleSearch(controller, { ...params, action: ACTIONS.SEARCH, page: 1 })
  );
};

const normalizeError = (error: any) => {
  if (!error) {
    return "เกิดข้อผิดพลาดในการค้นหาข้อมูล";
  }

  if (typeof error === "object") {
    return error;
  }

  const text = String(error);

  return text.includes("<!DOCTYPE") || text.includes("<html") || text.length > 300
    ? "เกิดข้อผิดพลาดในการค้นหาข้อมูล กรุณาตรวจสอบเงื่อนไขการค้นหา"
    : text;
};

const removeEmptyParams = (params: Record<string, any>) =>
  Object.fromEntries(
    Object.entries(params).filter(
      ([, value]) => value !== "" && value !== null && value !== undefined
    )
  );
